import { AlignJustify, Calculator, FileCode, FileJson, Globe, Hash, Mail, Network, Search, Shield, Wifi, Type, RefreshCw, Link, Code, Key, Lock, Fingerprint, Palette, QrCode, FileText, Table, Image, ImageIcon, Droplet, Minimize2, Clock, Calendar, Clock3, CalendarDays, Timer, TimerOff, TimerReset, Globe2, CalendarRange, CalendarCheck, BabyIcon, Briefcase, CalendarClock, DollarSign, ShieldCheck, FileJson as YamlIcon, FileJson as JsonIcon, FileCode as XmlIcon, Table as CsvIcon, Split, Wand2, Sparkles, GitMerge, BarChart3, Filter, ArrowUpDown, Group, TableProperties, Dices, Boxes, AlertTriangle, Scale, LineChart } from 'lucide-react';

export interface Tool {
  id: string;
  title: string;
  description: string;
  icon: typeof Calculator;
  link: string;
  isExternal: boolean;
  componentPath?: string;
}

export interface Category {
  id: string;
  name: string;
  description: string | null;
  tools: Tool[];
}

export const categories: Category[] = [
  {
    id: 'generator-tools',
    name: 'Generator Tools',
    description: 'Tools for generating various types of data',
    tools: [
      {
        id: 'uuid-generator',
        title: 'UUID Generator',
        description: 'Generate random UUIDs (v4) and timestamp-based UUIDs (v1)',
        icon: Fingerprint,
        link: '/tools/uuid/generate',
        isExternal: false,
        componentPath: 'uuid/UuidGenerator'
      },
      {
        id: 'color-generator',
        title: 'Color Generator',
        description: 'Generate color palettes and color codes in different formats',
        icon: Palette,
        link: '/tools/color/generate',
        isExternal: false,
        componentPath: 'color/ColorGenerator'
      },
      {
        id: 'qr-generator',
        title: 'QR Code Generator',
        description: 'Generate customizable QR codes with colors and error correction',
        icon: QrCode,
        link: '/tools/qr/generate',
        isExternal: false,
        componentPath: 'qr/QrGenerator'
      }
    ]
  },
  {
    id: 'password-tools',
    name: 'Password Tools',
    description: 'Tools for password generation and security analysis',
    tools: [
      {
        id: 'password-generator',
        title: 'Password Generator',
        description: 'Generate secure random passwords with customizable options',
        icon: Lock,
        link: '/tools/password/generate',
        isExternal: false,
        componentPath: 'password/PasswordGenerator'
      },
      {
        id: 'password-strength',
        title: 'Password Strength Checker',
        description: 'Test password security and get improvement suggestions',
        icon: Lock,
        link: '/tools/password/strength',
        isExternal: false,
        componentPath: 'password/PasswordStrength'
      }
    ]
  },
  {
    id: 'jwt-tools',
    name: 'JWT Tools',
    description: 'Tools for working with JSON Web Tokens',
    tools: [
      {
        id: 'jwt-decoder',
        title: 'JWT Decoder',
        description: 'Decode and inspect JWT tokens with header, payload, and signature analysis',
        icon: Key,
        link: '/tools/jwt/decode',
        isExternal: false,
        componentPath: 'jwt/JwtDecoder'
      },
      {
        id: 'jwt-generator',
        title: 'JWT Generator',
        description: 'Create custom JWT tokens with configurable headers and claims',
        icon: Key,
        link: '/tools/jwt/generate',
        isExternal: false,
        componentPath: 'jwt/JwtGenerator'
      }
    ]
  },
  {
    id: 'encoding-tools',
    name: 'Encoding Tools',
    description: 'Tools for encoding and decoding data',
    tools: [
      {
        id: 'base64-encoder',
        title: 'Base64 Encoder',
        description: 'Convert text to Base64 encoding with support for UTF-8 and file uploads',
        icon: Code,
        link: '/tools/encoding/base64-encode',
        isExternal: false,
        componentPath: 'encoding/Base64Encoder'
      },
      {
        id: 'base64-decoder',
        title: 'Base64 Decoder',
        description: 'Convert Base64 back to text with support for data URIs and file decoding',
        icon: Code,
        link: '/tools/encoding/base64-decode',
        isExternal: false,
        componentPath: 'encoding/Base64Decoder'
      }
    ]
  },
  {
    id: 'data-tools',
    name: 'Data Tools',
    description: 'Tools for data conversion and processing',
    tools: [
      {
        id: 'csv-to-json',
        title: 'CSV to JSON Converter',
        description: 'Convert CSV data to JSON format with customizable options',
        icon: Table,
        link: '/tools/data/csv-to-json',
        isExternal: false,
        componentPath: 'data/CsvToJson'
      },
      {
        id: 'json-to-csv',
        title: 'JSON to CSV Converter',
        description: 'Convert JSON arrays to CSV format with nested object support',
        icon: Table,
        link: '/tools/data/json-to-csv',
        isExternal: false,
        componentPath: 'data/JsonToCsv'
      },
      {
        id: 'csv-to-excel',
        title: 'CSV to Excel Converter',
        description: 'Convert CSV files to Excel format with formatting options',
        icon: Table,
        link: '/tools/data/csv-to-excel',
        isExternal: false,
        componentPath: 'data/CsvToExcel'
      },
      {
        id: 'excel-to-csv',
        title: 'Excel to CSV Converter',
        description: 'Convert Excel files to CSV format with sheet selection',
        icon: Table,
        link: '/tools/data/excel-to-csv',
        isExternal: false,
        componentPath: 'data/ExcelToCsv'
      },
      {
        id: 'yaml-to-json',
        title: 'YAML to JSON Converter',
        description: 'Convert YAML data to JSON format with customizable indentation',
        icon: YamlIcon,
        link: '/tools/data/yaml-to-json',
        isExternal: false,
        componentPath: 'data/YamlToJson'
      },
      {
        id: 'json-validator',
        title: 'JSON Validator',
        description: 'Validate and format JSON data with customizable indentation',
        icon: JsonIcon,
        link: '/tools/data/json-validator',
        isExternal: false,
        componentPath: 'data/JsonValidator'
      },
      {
        id: 'xml-validator',
        title: 'XML Validator',
        description: 'Validate and format XML documents with proper indentation',
        icon: XmlIcon,
        link: '/tools/data/xml-validator',
        isExternal: false,
        componentPath: 'data/XmlValidator'
      },
      {
        id: 'csv-validator',
        title: 'CSV Validator',
        description: 'Validate CSV data with support for different delimiters',
        icon: CsvIcon,
        link: '/tools/data/csv-validator',
        isExternal: false,
        componentPath: 'data/CsvValidator'
      },
      {
        id: 'data-diff',
        title: 'Data Diff Tool',
        description: 'Compare and find differences between JSON, XML, or CSV datasets',
        icon: Split,
        link: '/tools/data/diff',
        isExternal: false,
        componentPath: 'data/DataDiff'
      },
      {
        id: 'data-transformer',
        title: 'Data Transformer',
        description: 'Transform data with custom rules like renaming, formatting, combining fields and more',
        icon: Wand2,
        link: '/tools/data/transformer',
        isExternal: false,
        componentPath: 'data/DataTransformer'
      },
      {
        id: 'data-cleaner',
        title: 'Data Cleaner',
        description: 'Clean and standardize data by removing duplicates, normalizing formats, and handling missing values',
        icon: Sparkles,
        link: '/tools/data/cleaner',
        isExternal: false,
        componentPath: 'data/DataCleaner'
      },
      {
        id: 'data-merger',
        title: 'Data Merger',
        description: 'Merge multiple datasets using different join strategies based on common fields',
        icon: GitMerge,
        link: '/tools/data/merger',
        isExternal: false,
        componentPath: 'data/DataMerger'
      },
      {
        id: 'data-aggregator',
        title: 'Data Aggregator',
        description: 'Aggregate and summarize data with operations like count, sum, average, and more',
        icon: BarChart3,
        link: '/tools/data/aggregator',
        isExternal: false,
        componentPath: 'data/DataAggregator'
      },
      {
        id: 'data-filter',
        title: 'Data Filter',
        description: 'Filter datasets using custom rules with multiple operators and conditions',
        icon: Filter,
        link: '/tools/data/filter',
        isExternal: false,
        componentPath: 'data/DataFilter'
      },
      {
        id: 'data-sorter',
        title: 'Data Sorter',
        description: 'Sort datasets by multiple fields with custom sort orders and data types',
        icon: ArrowUpDown,
        link: '/tools/data/sorter',
        isExternal: false,
        componentPath: 'data/DataSorter'
      },
      {
        id: 'data-grouper',
        title: 'Data Grouper',
        description: 'Group and organize data by multiple fields with nested grouping and summary statistics',
        icon: Group,
        link: '/tools/data/grouper',
        isExternal: false,
        componentPath: 'data/DataGrouper'
      },
      {
        id: 'data-pivot',
        title: 'Data Pivot',
        description: 'Create pivot tables and cross-tabulations with custom aggregations and groupings',
        icon: TableProperties,
        link: '/tools/data/pivot',
        isExternal: false,
        componentPath: 'data/DataPivot'
      },
      {
        id: 'data-sampling',
        title: 'Data Sampling',
        description: 'Generate random or stratified samples from datasets with reproducible results',
        icon: Dices,
        link: '/tools/data/sampling',
        isExternal: false,
        componentPath: 'data/DataSampling'
      },
      {
        id: 'data-binning',
        title: 'Data Binning',
        description: 'Group numerical data into intervals with equal width, frequency, or custom breaks',
        icon: Boxes,
        link: '/tools/data/binning',
        isExternal: false,
        componentPath: 'data/DataBinning'
      },
      {
        id: 'data-outliers',
        title: 'Data Outlier Detection',
        description: 'Identify and analyze outliers using statistical methods like Z-score, IQR, and modified Z-score',
        icon: AlertTriangle,
        link: '/tools/data/outliers',
        isExternal: false,
        componentPath: 'data/DataOutliers'
      },
      {
        id: 'data-normalization',
        title: 'Data Normalization',
        description: 'Normalize numerical data using min-max scaling, z-score standardization, or robust scaling',
        icon: Scale,
        link: '/tools/data/normalization',
        isExternal: false,
        componentPath: 'data/DataNormalization'
      },
      {
        id: 'data-correlation',
        title: 'Data Correlation Analysis',
        description: 'Analyze relationships between numerical variables using Pearson and Spearman correlations',
        icon: LineChart,
        link: '/tools/data/correlation',
        isExternal: false,
        componentPath: 'data/DataCorrelation'
      },
      {
        id: 'data-regression',
        title: 'Data Regression Analysis',
        description: 'Analyze and predict relationships between variables using linear and polynomial regression',
        icon: LineChart,
        link: '/tools/data/regression',
        isExternal: false,
        componentPath: 'data/DataRegression'
      },
      {
        id: 'data-clustering',
        title: 'Data Clustering Analysis',
        description: 'Group similar data points using K-means and hierarchical clustering algorithms',
        icon: LineChart,
        link: '/tools/data/clustering',
        isExternal: false,
        componentPath: 'data/DataClustering'
      },
      {
        id: 'data-dimensionality',
        title: 'Data Dimensionality Reduction',
        description: 'Reduce high-dimensional data using PCA and t-SNE techniques',
        icon: LineChart,
        link: '/tools/data/dimensionality',
        isExternal: false,
        componentPath: 'data/DataDimensionality'
      },
      {
        id: 'data-forecasting',
        title: 'Time Series Forecasting',
        description: 'Predict future values using exponential smoothing methods',
        icon: LineChart,
        link: '/tools/data/forecasting',
        isExternal: false,
        componentPath: 'data/DataForecasting'
      },
      {
        id: 'data-anomaly',
        title: 'Data Anomaly Detection',
        description: 'Identify anomalies using statistical and machine learning methods',
        icon: AlertTriangle,
        link: '/tools/data/anomaly',
        isExternal: false,
        componentPath: 'data/DataAnomaly'
      },
      {
        id: 'data-segmentation',
        title: 'Data Segmentation',
        description: 'Divide data into meaningful segments using various methods',
        icon: Boxes,
        link: '/tools/data/segmentation',
        isExternal: false,
        componentPath: 'data/DataSegmentation'
      },
      {
        id: 'data-seasonality',
        title: 'Seasonality Analysis',
        description: 'Analyze and decompose seasonal patterns in time series data',
        icon: LineChart,
        link: '/tools/data/seasonality',
        isExternal: false,
        componentPath: 'data/DataSeasonality'
      },
      {
        id: 'data-smoothing',
        title: 'Data Smoothing',
        description: 'Smooth noisy data using various methods like moving average and kernel smoothing',
        icon: LineChart,
        link: '/tools/data/smoothing',
        isExternal: false,
        componentPath: 'data/DataSmoothing'
      },
      {
        id: 'data-interpolation',
        title: 'Data Interpolation',
        description: 'Fill missing values in time series data using various interpolation methods',
        icon: LineChart,
        link: '/tools/data/interpolation',
        isExternal: false,
        componentPath: 'data/DataInterpolation'
      },
      {
        id: 'data-imputation',
        title: 'Data Imputation',
        description: 'Handle missing values using statistical methods like mean, median, KNN, and regression',
        icon: LineChart,
        link: '/tools/data/imputation',
        isExternal: false,
        componentPath: 'data/DataImputation'
      },
      {
        id: 'data-encoding',
        title: 'Data Encoding',
        description: 'Convert data between different encoding formats like Base64, Hex, Binary, URL, HTML, and Unicode',
        icon: Code,
        link: '/tools/data/encoding',
        isExternal: false,
        componentPath: 'data/DataEncoding'
      }
    ]
  },
  {
    id: 'developer-tools',
    name: 'Developer Tools',
    description: 'Tools for code formatting and development',
    tools: [
      {
        id: 'code-beautifier',
        title: 'Javascript Code Beautifier',
        description: 'Format and beautify JavaScript, TypeScript, JSX, and TSX code with customizable options',
        icon: FileCode,
        link: '/tools/code/javascript-beautifier',
        isExternal: false,
        componentPath: 'code/CodeBeautifier'
      },
      {
        id: 'svg-to-png',
        title: 'SVG to PNG Converter',
        description: 'Convert SVG files or code to PNG format with customizable dimensions',
        icon: Image,
        link: '/tools/code/svg-to-png',
        isExternal: false,
        componentPath: 'code/SvgToPng'
      },
      {
        id: 'image-converter',
        title: 'Image Format Converter',
        description: 'Convert images between different formats (PNG, JPG, WebP) with quality control',
        icon: ImageIcon,
        link: '/tools/code/image-converter',
        isExternal: false,
        componentPath: 'code/ImageConverter'
      },
      {
        id: 'image-color-extractor',
        title: 'Image Color Extractor',
        description: 'Extract dominant colors and create a color palette from any image',
        icon: Droplet,
        link: '/tools/code/image-colors',
        isExternal: false,
        componentPath: 'code/ImageColorExtractor'
      },
      {
        id: 'code-minifier',
        title: 'Code Minifier',
        description: 'Minify JavaScript, CSS, and HTML code by removing unnecessary characters',
        icon: Minimize2,
        link: '/tools/code/minifier',
        isExternal: false,
        componentPath: 'code/CodeMinifier'
      },
      {
        id: 'cron-generator',
        title: 'Cron Expression Generator',
        description: 'Build cron expressions visually for scheduling tasks',
        icon: Clock,
        link: '/tools/code/cron',
        isExternal: false,
        componentPath: 'code/CronGenerator'
      }
    ]
  },
  {
    id: 'text-tools',
    name: 'Text Tools',
    description: 'Tools for text processing and formatting',
    tools: [
      {
        id: 'markdown-preview',
        title: 'Markdown Preview',
        description: 'Live markdown preview with GitHub Flavored Markdown support',
        icon: FileText,
        link: '/tools/text/markdown',
        isExternal: false,
        componentPath: 'text/MarkdownPreview'
      },
      {
        id: 'text-statistics',
        title: 'Text Statistics',
        description: 'Analyze text with word count, readability scores, and detailed statistics',
        icon: FileText,
        link: '/tools/text/statistics',
        isExternal: false,
        componentPath: 'text/TextStatistics'
      },
      {
        id: 'text-diff',
        title: 'Text Diff Tool',
        description: 'Compare two texts and highlight differences with multiple comparison modes',
        icon: FileText,
        link: '/tools/text/diff',
        isExternal: false,
        componentPath: 'text/TextDiff'
      },
      {
        id: 'unicode-explorer',
        title: 'Unicode Character Explorer',
        description: 'Explore Unicode characters, view information, and convert between formats',
        icon: FileText,
        link: '/tools/text/unicode',
        isExternal: false,
        componentPath: 'text/UnicodeExplorer'
      },
      {
        id: 'text-encoding',
        title: 'Text Encoding Converter',
        description: 'Convert text between different encodings with support for special characters',
        icon: FileText,
        link: '/tools/text/encoding',
        isExternal: false,
        componentPath: 'text/TextEncoding'
      },
      {
        id: 'search-replace',
        title: 'Advanced Search and Replace',
        description: 'Powerful regex-based search and replace with live preview and pattern library',
        icon: FileText,
        link: '/tools/text/search-replace',
        isExternal: false,
        componentPath: 'text/SearchReplace'
      }
    ]
  },
  {
    id: 'proxy-tools',
    name: 'Proxy Tools',
    description: 'Tools for proxy and network testing',
    tools: [
      {
        id: 'proxy-checker',
        title: 'Online Proxy Checker',
        description: 'Check proxy speed, anonymity, and protocols',
        icon: Shield,
        link: 'https://proxyscrape.com/online-proxy-checker',
        isExternal: true
      },
      {
        id: 'proxy-list',
        title: 'Free Proxy List',
        description: 'Free proxy lists updated every 5 minutes',
        icon: Shield,
        link: 'https://proxyscrape.com/free-proxy-list',
        isExternal: true
      },
      {
        id: 'proxy-checker-app',
        title: 'Proxy Checker',
        description: 'Advanced proxy testing application',
        icon: Shield,
        link: 'https://proxyscrape.com/proxy-checker',
        isExternal: true
      },
      {
        id: 'proxy-filter',
        title: 'Proxy Filter',
        description: 'Filter and sort proxy lists',
        icon: Shield,
        link: 'https://proxyscrape.com/proxy-filter',
        isExternal: true
      }
    ]
  },
  {
    id: 'time-tools',
    name: 'Time & Date Tools',
    description: 'Tools for working with dates, times, and timestamps',
    tools: [
      {
        id: 'timestamp-converter',
        title: 'Timestamp Converter',
        description: 'Convert timestamps between Unix, ISO 8601, RFC, and custom formats',
        icon: Calendar,
        link: '/tools/time/timestamp',
        isExternal: false,
        componentPath: 'time/TimestampConverter'
      },
      {
        id: 'timezone-converter',
        title: 'Time Zone Converter',
        description: 'Convert times between different time zones with support for major global regions',
        icon: Clock3,
        link: '/tools/time/timezone',
        isExternal: false,
        componentPath: 'time/TimeZoneConverter'
      },
      {
        id: 'date-calculator',
        title: 'Date Calculator',
        description: 'Calculate time durations or add/subtract time from dates',
        icon: CalendarDays,
        link: '/tools/time/calculator',
        isExternal: false,
        componentPath: 'time/DateCalculator'
      },
      {
        id: 'countdown-timer',
        title: 'Countdown Timer',
        description: 'Set and track countdown timers with notifications when complete',
        icon: Timer,
        link: '/tools/time/countdown',
        isExternal: false,
        componentPath: 'time/CountdownTimer'
      },
      {
        id: 'stopwatch-timer',
        title: 'Stopwatch Timer',
        description: 'Track elapsed time with lap timing functionality',
        icon: TimerOff,
        link: '/tools/time/stopwatch',
        isExternal: false,
        componentPath: 'time/StopwatchTimer'
      },
      {
        id: 'pomodoro-timer',
        title: 'Pomodoro Timer',
        description: 'Stay focused using the Pomodoro Technique with customizable work and break intervals',
        icon: TimerReset,
        link: '/tools/time/pomodoro',
        isExternal: false,
        componentPath: 'time/PomodoroTimer'
      },
      {
        id: 'world-clock',
        title: 'World Clock',
        description: 'View current times in multiple time zones simultaneously',
        icon: Globe2,
        link: '/tools/time/world',
        isExternal: false,
        componentPath: 'time/WorldClock'
      },
      {
        id: 'calendar-generator',
        title: 'Calendar Generator',
        description: 'Generate printable calendars with customizable options and holiday markings',
        icon: CalendarRange,
        link: '/tools/time/calendar',
        isExternal: false,
        componentPath: 'time/CalendarGenerator'
      },
      {
        id: 'date-formatter',
        title: 'Date Formatter',
        description: 'Format dates in various standard and custom formats with support for multiple locales',
        icon: CalendarCheck,
        link: '/tools/time/format',
        isExternal: false,
        componentPath: 'time/DateFormatter'
      },
      {
        id: 'age-calculator',
        title: 'Age Calculator',
        description: 'Calculate age and time differences between dates with zodiac sign information',
        icon: BabyIcon,
        link: '/tools/time/age',
        isExternal: false,
        componentPath: 'time/AgeCalculator'
      },
      {
        id: 'business-days-calculator',
        title: 'Business Days Calculator',
        description: 'Calculate working days between dates, excluding weekends and holidays',
        icon: Briefcase,
        link: '/tools/time/business',
        isExternal: false,
        componentPath: 'time/BusinessDaysCalculator'
      },
      {
        id: 'week-number-calculator',
        title: 'Week Number Calculator',
        description: 'Calculate ISO week number and get detailed week information for any date',
        icon: CalendarClock,
        link: '/tools/time/week',
        isExternal: false,
        componentPath: 'time/WeekNumberCalculator'
      },
      {
        id: 'fiscal-year-calculator',
        title: 'Fiscal Year Calculator',
        description: 'Calculate fiscal year dates, periods, and working days for financial planning',
        icon: DollarSign,
        link: '/tools/time/fiscal',
        isExternal: false,
        componentPath: 'time/FiscalYearCalculator'
      }
    ]
  },
  {
    id: 'network-tools',
    name: 'Network Tools',
    description: 'Tools for network analysis and testing',
    tools: [
      {
        id: 'ssl-checker',
        title: 'SSL Certificate Checker',
        description: 'Analyze SSL certificates, expiration dates, and security details',
        icon: ShieldCheck,
        link: '/tools/network/ssl-checker',
        isExternal: false,
        componentPath: 'network/SslChecker'
      },
      {
        id: 'dns-lookup',
        title: 'DNS Lookup Tool',
        description: 'Perform DNS lookups and view server locations',
        icon: Search,
        link: '/tools/dns',
        isExternal: false,
        componentPath: 'DnsLookup'
      },
      {
        id: 'ipv4-ping',
        title: 'IPv4 Ping Test Online',
        description: 'Test connectivity to IPv4 hosts',
        icon: Network,
        link: '/tools/ping/ipv4',
        isExternal: false,
        componentPath: 'Ipv4PingTester'
      },
      {
        id: 'ipv6-ping',
        title: 'IPv6 Ping Test Online',
        description: 'Test connectivity to IPv6 hosts',
        icon: Network,
        link: '/tools/ping/ipv6',
        isExternal: false,
        componentPath: 'Ipv6PingTester'
      },
      {
        id: 'traceroute',
        title: 'Online Traceroute Tool',
        description: 'Trace network paths from global locations',
        icon: Network,
        link: '/tools/traceroute',
        isExternal: false,
        componentPath: 'TracerouteTester'
      },
      {
        id: 'http-headers',
        title: 'HTTP Header Viewer',
        description: 'View HTTP headers of any URL',
        icon: Globe,
        link: '/tools/http-headers',
        isExternal: false,
        componentPath: 'HttpHeaderViewer'
      },
      {
        id: 'json-converter',
        title: 'JSON Converter',
        description: 'Convert between JavaScript objects and JSON format',
        icon: FileJson,
        link: '/tools/json',
        isExternal: false,
        componentPath: 'JsonConverter'
      },
      {
        id: 'json-to-php',
        title: 'JSON to PHP Array',
        description: 'Convert JSON to PHP array format',
        icon: FileCode,
        link: '/tools/json-to-php',
        isExternal: false,
        componentPath: 'PhpConverter'
      },
      {
        id: 'php-to-json',
        title: 'PHP Array to JSON',
        description: 'Convert PHP arrays to JSON format',
        icon: FileCode,
        link: '/tools/php-to-json',
        isExternal: false,
        componentPath: 'PhpToJsonConverter'
      },
      {
        id: 'port-generator',
        title: 'Port Generator',
        description: 'Generate random TCP/UDP port numbers',
        icon: Network,
        link: '/tools/port',
        isExternal: false,
        componentPath: 'PortGenerator'
      },
      {
        id: 'ipv6-generator',
        title: 'IPv6 Generator',
        description: 'Create random IPv6 addresses',
        icon: Wifi,
        link: '/tools/ipv6',
        isExternal: false,
        componentPath: 'Ipv6Generator'
      },
      {
        id: 'email-generator',
        title: 'Email Generator',
        description: 'Generate random email addresses',
        icon: Mail,
        link: '/tools/email',
        isExternal: false,
        componentPath: 'EmailGenerator'
      },
      {
        id: 'mac-generator',
        title: 'MAC Generator',
        description: 'Generate random MAC addresses with custom formats',
        icon: Hash,
        link: '/tools/mac',
        isExternal: false,
        componentPath: 'MacGenerator'
      },
      {
        id: 'md5-generator',
        title: 'MD5 Hash Generator',
        description: 'Generate MD5 hashes from text',
        icon: Hash,
        link: '/tools/hash/md5',
        isExternal: false,
        componentPath: 'hash/Md5Generator'
      },
      {
        id: 'sha1-generator',
        title: 'SHA1 Hash Generator',
        description: 'Generate SHA1 hashes from text',
        icon: Hash,
        link: '/tools/hash/sha1',
        isExternal: false,
        componentPath: 'hash/Sha1Generator'
      },
      {
        id: 'sha224-generator',
        title: 'SHA224 Hash Generator',
        description: 'Generate SHA224 hashes from text',
        icon: Hash,
        link: '/tools/hash/sha224',
        isExternal: false,
        componentPath: 'hash/Sha224Generator'
      },
      {
        id: 'sha256-generator',
        title: 'SHA256 Hash Generator',
        description: 'Generate SHA256 hashes from text',
        icon: Hash,
        link: '/tools/hash/sha256',
        isExternal: false,
        componentPath: 'hash/Sha256Generator'
      },
      {
        id: 'sha384-generator',
        title: 'SHA384 Hash Generator',
        description: 'Generate SHA384 hashes from text',
        icon: Hash,
        link: '/tools/hash/sha384',
        isExternal: false,
        componentPath: 'hash/Sha384Generator'
      },
      {
        id: 'sha512-generator',
        title: 'SHA512 Hash Generator',
        description: 'Generate SHA512 hashes from text',
        icon: Hash,
        link: '/tools/hash/sha512',
        isExternal: false,
        componentPath: 'hash/Sha512Generator'
      },
      {
        id: 'ipv4-cidr',
        title: 'IPv4 CIDR Calculator',
        description: 'Calculate IPv4 subnet ranges and information',
        icon: Calculator,
        link: '/tools/cidr',
        isExternal: false,
        componentPath: 'CidrCalculator'
      },
      {
        id: 'ipv6-cidr',
        title: 'IPv6 CIDR Calculator',
        description: 'Calculate IPv6 subnet ranges and information',
        icon: Calculator,
        link: '/tools/ipv6-cidr',
        isExternal: false,
        componentPath: 'Ipv6CidrCalculator'
      },
      {
        id: 'line-counter',
        title: 'Line Counter',
        description: 'Count lines containing specific text or patterns',
        icon: AlignJustify,
        link: '/tools/line-counter',
        isExternal: false,
        componentPath: 'LineCounter'
      },
      {
        id: 'email-extractor',
        title: 'Email Extractor',
        description: 'Extract email addresses from text',
        icon: Mail,
        link: '/tools/email-extractor',
        isExternal: false,
        componentPath: 'EmailExtractor'
      },
      {
        id: 'ip-extractor',
        title: 'IP Extractor',
        description: 'Extract IPv4 and IPv6 addresses from text',
        icon: Search,
        link: '/tools/ip-extractor',
        isExternal: false,
        componentPath: 'IpExtractor'
      },
      {
        id: 'random-ip',
        title: 'Random IP Generator',
        description: 'Generate random IPv4 and IPv6 addresses',
        icon: RefreshCw,
        link: '/tools/random-ip',
        isExternal: false,
        componentPath: 'RandomIpGenerator'
      },
      {
        id: 'random-proxy',
        title: 'Random Proxy Generator',
        description: 'Generate random proxy addresses with custom ports',
        icon: RefreshCw,
        link: '/tools/random-proxy',
        isExternal: false,
        componentPath: 'RandomProxyGenerator'
      },
      {
        id: 'text-prefix-suffix',
        title: 'Add Prefix or Suffix',
        description: 'Add text before or after each line',
        icon: Type,
        link: '/tools/prefix-suffix',
        isExternal: false,
        componentPath: 'TextPrefixSuffix'
      },
      {
        id: 'newline-converter',
        title: 'Convert New Lines to Spaces',
        description: 'Convert line breaks to spaces with options',
        icon: Type,
        link: '/tools/newline-converter',
        isExternal: false,
        componentPath: 'NewlineConverter'
      },
      {
        id: 'url-extractor',
        title: 'URL Extractor',
        description: 'Extract URLs from text or HTML content',
        icon: Link,
        link: '/tools/url-extractor',
        isExternal: false,
        componentPath: 'UrlExtractor'
      },
      {
        id: 'case-converter',
        title: 'Case Converter',
        description: 'Convert text between different cases: camelCase, PascalCase, snake_case, and more',
        icon: Type,
        link: '/tools/string/case',
        isExternal: false,
        componentPath: 'string/CaseConverter'
      },
      {
        id: 'string-compare',
        title: 'String Comparison',
        description: 'Compare two texts and find differences with character, word, or line comparison',
        icon: Type,
        link: '/tools/string/compare',
        isExternal: false,
        componentPath: 'string/StringCompare'
      },
      {
        id: 'regex-tester',
        title: 'Regex Pattern Tester',
        description: 'Test and debug regular expressions with real-time matching and pattern library',
        icon: Search,
        link: '/tools/string/regex',
        isExternal: false,
        componentPath: 'string/RegexTester'
      }
    ]
  }
];

// Create a map of routes to component paths
export const routeComponentMap = Object.fromEntries(
  categories.flatMap(category => 
    category.tools
      .filter(tool => !tool.isExternal && tool.componentPath)
      .map(tool => [tool.link, tool.componentPath])
  )
);
